import { useNavigate } from 'react-router-dom';
import { Button, Icons } from './ui.tsx';

/**
 * Shown instead of an editor that a phone cannot usefully run.
 *
 * The spreadsheet grid and the document editor are built around a pointer, a
 * keyboard and a lot of horizontal room: the grid alone runs some 2,400px past
 * the right edge of a phone. Rendering it anyway would give the client a
 * mangled screen and no explanation, so this says plainly what happened and
 * offers the way back.
 *
 * Everything else in the system — the Drive, the dashboard, the admin pages —
 * works on a phone, which is why this is a message about one screen rather
 * than a message about the product.
 */
export function NeedsLargerScreen({ what }: { what: 'spreadsheet' | 'document' }) {
  const navigate = useNavigate();

  return (
    <div className="flex h-full flex-col items-center justify-center px-6 py-10 text-center">
      <span className="mb-4 rounded-full bg-sunken p-4 text-muted" aria-hidden>
        {what === 'spreadsheet' ? <Icons.Sheet /> : <Icons.File />}
      </span>

      <h1 className="text-body font-medium text-ink">
        {what === 'spreadsheet' ? 'Spreadsheets' : 'Documents'} need a larger screen
      </h1>

      <p className="mt-2 max-w-xs text-body-sm text-muted">
        The editor is built for a keyboard and a wide window. Open this on a laptop or a tablet in
        landscape and it will be here waiting.
      </p>

      <p className="mt-1 max-w-xs text-micro text-subtle">
        Everything else — the Drive, uploads, and the rest — works fine on your phone.
      </p>

      <Button className="mt-6" icon={<Icons.Folder />} onClick={() => navigate('/drive')}>
        Back to the Drive
      </Button>
    </div>
  );
}
